import * as React from 'react';
import {createViewConfig, dynamic} from '@sail/engine';
import {tokens} from '@sail/tokens';
import type {Href} from '@sail/global-state';
import {Action} from './Action';
import {Spinner, SpinnerConfig} from './Spinner';
import type {ActionProps} from './Action';
import {css} from '../css';
import {IconConfig} from './Icon';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';
import {useUiConfig} from '../providers/UiConfigProvider';
import useDebouncedValue from './utils/useDebouncedValue';

// Imperatively inserts a hidden <button type="submit|reset"> as a sibling before
// the <a> element. We avoid declarative JSX to avoid structure changes:
// imperative insertion leaves props.children unmodified.
// Placing the button outside <a> also avoids nesting interactive content.
// https://jira.corp.stripe.com/browse/RUN_SAILUI-708
function useHiddenFormButton(
  enabled: boolean,
  behavior: 'submit' | 'reset' | undefined,
  form: string | undefined,
  actionRef: React.RefObject<HTMLElement | null>,
) {
  React.useEffect(() => {
    const container = actionRef.current?.parentNode;

    if (!enabled || !behavior || !container) {
      return;
    }

    const btn = Object.assign(document.createElement('input'), {
      type: behavior,
      hidden: true,
    });

    btn.setAttribute('aria-hidden', 'true');
    btn.tabIndex = -1;
    if (form) {
      btn.setAttribute('form', form);
    }

    container.insertBefore(btn, actionRef.current);

    return () => {
      btn.remove();
    };
  }, [enabled, behavior, form, actionRef]);
}

export type ButtonProps = {
  /**
   * The size of the button
   * @external
   */
  size: 'small' | 'medium' | 'large';

  /**
   * The type of the button
   * @external
   */
  type: 'primary' | 'secondary' | 'destructive';

  /**
   * Allows the button to submit or reset a form
   * @external
   */
  behavior?: 'submit' | 'reset';

  /**
   * Sets the visual style to pending and functionally disables the button
   * @external
   */
  pending?: boolean;

  /**
   * The id of the form element to associate the button with (if it's not an ancestor of the button)
   */
  form?: string;

  /**
   * Content concisely describing the button's functionality.
   */
  children?: React.ReactNode;

  href?: Href;
} & Omit<ActionProps, 'children'>;

const buttonSizeToIconSizeMap = {
  small: 'xsmall' as const,
  medium: 'xsmall' as const,
  large: 'small' as const,
};

const pendingSpinnerCss = css({
  position: 'absolute',
  top: `calc(50% - ${tokens['space.small']})`,
});

/**
 * @param autofocus
 * @param className
 * @param disabled
 * @param form
 * @param name
 * @param onPress
 * @param type
 * @param value
 * @param target
 */
export const ButtonConfig = createViewConfig({
  name: 'Button',
  props: {} as ButtonProps,
  flattens: true,
  defaults: {
    size: 'medium',
    type: 'secondary',
  },
});

/**
 * Buttons let users take action.
 */
export const Button = ButtonConfig.createView(
  ({behavior, form, pending, type, ...props}) => {
    // `pending` is *visually* debounced in order to avoid overly-quick flashes
    // of visual styles, but the disabled behvior is immediately applied
    const isVisuallyPending = useDebouncedValue(pending ?? false, 100);

    const mediaQuery = useMediaQuery();
    const isMobile = mediaQuery?.isTouch;
    const uiConfig = useUiConfig();
    const implicitFormSubmissionEnabled =
      !!uiConfig?.enableImplicitFormSubmission;

    const buttonRemoveSetTimeoutEnabled =
      !!uiConfig?.enableButtonRemoveSetTimeout;

    // OnPress generates phantom clicks for mobiles (see https://go.corp.stripe.com/sail-touch-device-support-doc for more info)
    const onBehavior = React.useCallback(
      (fn: () => void) => {
        if (isMobile && !buttonRemoveSetTimeoutEnabled) {
          // Tested on a physical device and 2ms seems to be enough but 10ms is set for safety and delay is not noticeable.
          return setTimeout(fn, 10);
        }
        return fn();
      },
      [isMobile, buttonRemoveSetTimeoutEnabled],
    );

    const actionRef = React.useRef<HTMLAnchorElement>(null);
    const shouldInsertHiddenButton =
      implicitFormSubmissionEnabled &&
      behavior === 'submit' &&
      !pending &&
      !props.disabled;
    useHiddenFormButton(shouldInsertHiddenButton, behavior, form, actionRef);

    const resultingProps = {
      ...props,
      children:
        // We don't want to debounce turning off the spinner, so it needs both to be true
        isVisuallyPending && pending ? (
          <>
            {props.children}
            <Spinner />
          </>
        ) : (
          props.children
        ),
      onPress: pending ? undefined : props.onPress,
      behavior: pending ? undefined : behavior,
      ...(pending ? {'aria-disabled': true} : {}),
      ...(behavior && form ? {form} : {}),
    };

    return (
      <Action
        ref={actionRef}
        {...resultingProps}
        {...(isVisuallyPending
          ? {'data-state-isvisuallypending': 'true'}
          : undefined)}
        {...(behavior && !pending
          ? {
              onPress: () => {
                onBehavior(() => {
                  const button = Object.assign(
                    document.createElement('input'),
                    {
                      type: behavior,
                      hidden: true,
                      'aria-hidden': 'true',
                    },
                  );
                  form && button.setAttribute('form', form);
                  actionRef.current?.parentNode?.insertBefore(
                    button,
                    actionRef.current,
                  );
                  button.click();
                  button.remove();
                });
              },
            }
          : {})}
      />
    );
  },
  {
    uses: [
      // TODO(koop): investigate if we can remove ButtonProps (used to avoid size being inferred as optional)
      dynamic(({size, type, pending}: ButtonProps) => [
        IconConfig.customize({
          defaults: {size: buttonSizeToIconSizeMap[size]},
        }),
        SpinnerConfig.customize({
          css: [
            pending ? pendingSpinnerCss : css({}),
            type === 'secondary'
              ? css({fill: 'subdued'})
              : css({fill: 'onAction.primary'}),
          ],
        }),
      ]),
    ],

    css: {
      stack: 'x',
      display: 'inline',
      alignX: 'center',
      alignY: 'center',
      whiteSpace: 'nowrap',
      fontWeight: 'semibold',
      position: 'relative',
      zIndex: 0,
      borderRadius: 'action',
      transitionProperty: 'background-color, box-shadow',
      transitionDuration: 'medium',
      transitionTimingFunction: 'easing',
      height: 'fit',
      width: 'fit',
      ':hover': {
        transitionDuration: 'instant',
      },
      ':focus': {
        focusRing: 'focus',
      },
    },

    forward: {
      disabled: true,
      pending: true,
      type: true,
    },

    variants: {
      disabled: {
        true: css({
          cursor: 'default',
          '::after': {
            display: 'block',
            content: '""',
            position: 'absolute',
            top: '-1px',
            left: '-1px',
            bottom: '-1px',
            right: '-1px',
            borderRadius: 'action',
            zIndex: 1,
            backgroundColor: 'surface',
            pointerEvents: 'none',
            opacity: 0.5,
            margin: 'space.0',
          },
        }),
      },

      size: {
        small: css({
          font: 'label.small.emphasized',
          minHeight: 'large',
          paddingX: 'small',
          paddingY: 'xsmall',
          gap: 'xsmall',
        }),
        medium: css({
          font: 'label.medium.emphasized',
          minHeight: 'space.350',
          paddingX: 'small',
          paddingY: 'xsmall',
          gap: 'space.75',
        }),
        large: css({
          font: 'label.large.emphasized',
          minHeight: 'space.500',
          paddingX: 'medium',
          paddingY: 'small',
          gap: 'small',
        }),
      },

      type: {
        primary: css({
          color: 'onAction.primary',
          keyline: 'action.primary',
          ':hover:not(:active):not([aria-disabled="true"])': {
            backgroundColor: 'action.primary.hovered',
            keyline: 'action.primary.hovered',
            color: 'onAction.primary.hovered',
          },
          ':active:not([aria-disabled="true"])': {
            backgroundColor: 'action.primary.pressed',
            keyline: 'action.primary.pressed',
            color: 'onAction.primary.pressed',
          },
        }),
        secondary: css({
          color: 'onAction.secondary',
          keyline: 'action.secondary',
          ':hover:not(:active):not([aria-disabled="true"])': {
            backgroundColor: 'action.secondary.hovered',
            keyline: 'action.secondary.hovered',
            color: 'onAction.secondary.hovered',
          },
          ':active:not([aria-disabled="true"])': {
            backgroundColor: 'action.secondary.pressed',
            color: 'onAction.secondary.pressed',
            keyline: 'action.secondary.pressed',
          },
        }),
        destructive: css({
          color: 'onAction.destructive',
          keyline: 'action.destructive',
          ':hover:not(:active):not([aria-disabled="true"])': {
            backgroundColor: 'action.destructive.hovered',
            keyline: 'action.destructive.hovered',
            color: 'onAction.destructive.hovered',
          },
          ':active:not([aria-disabled="true"])': {
            backgroundColor: 'action.destructive.pressed',
            keyline: 'action.destructive.pressed',
            color: 'onAction.destructive.pressed',
          },
        }),
      },

      pending: {
        // TODO(alex): Replace once we have state-based variants
        true: css.global({
          '[data-state-isvisuallypending="true"]': {
            color: 'rgba(0,0,0,0) !important',
          },
          '[data-state-isvisuallypending="true"] > *': {
            opacity: '0 !important',
          },
          '[data-state-isvisuallypending="true"] > :last-child': {
            opacity: '1 !important',
          },
        }),
      },
    },
  },
);
